
import BCXInfoSection from '@/components/molecules/BCXInfoSection.vue';
import BCXMarkdown from '@/components/molecules/BCXMarkdown.vue';
import BCXMarkdownPopup from '@/components/molecules/BCXMarkdownPopup.vue';
import BCXCheckbox from '@/components/molecules/forms/BCXCheckbox.vue';
import EditIcon from '@/components/molecules/EditIcon.vue';
import InvitesIcon from '@/components/svg/invites-partner-icon2.svg?inline';
import { InviteCode, InviteCodesResponse } from '@/models/Invitation';
import { getInviteCodesOrThrow } from '@/services/Invitation/Api';
import { getAffiliateContract, signAffiliateContract } from '@/services/AffiliateService';
import usePageState from '@/state/pageState';
import sortComp from '@/utils/sortComp';
import getAxiosResponseErrorCode from '@/utils/getAxiosResponseErrorCode';
import useUser from '@/mixins/useUser';
import usePopupState from '@/state/usePopupState';
import useSettings from '@/state/settingsState';
import copy from 'copy-text-to-clipboard';
import {
  computed, defineComponent, onBeforeUnmount, onMounted, reactive, ref
} from 'vue';
import { getInviteCodeUrl } from '@/views/invites/utils/useInviteTools';
import partnerBannerDesktop1x from '@/assets/img/partnerpage/insider_banner_dektop_1x.png';
import partnerBannerDesktop2x from '@/assets/img/partnerpage/insider_banner_dektop_2x.png';
import partnerBannerMobile1x from '@/assets/img/partnerpage/insider_banner_mobile_1x.png';
import partnerBannerMobile2x from '@/assets/img/partnerpage/insider_banner_mobile_2x.png';
import PartnerProgramCodesSkeleton from '@/views/invites/PartnerProgramCodesSkeleton.vue';

type MaterialItem = {
  labelKey: string;
  url: string;
  filename: string;
};

export default defineComponent({
  name: 'PartnerProgramCodes',
  components: {
    BCXMarkdown,
    BCXMarkdownPopup,
    BCXInfoSection,
    BCXCheckbox,
    EditIcon,
    InvitesIcon,
    PartnerProgramCodesSkeleton,
  },
  setup() {
    const materialItems: MaterialItem[] = [
      {
        labelKey: 'invites.materials.brochure2',
        url: '/ByteCookie_Info_Onepager_DE.pdf',
        filename: 'ByteCookie_Info_Onepager_DE.pdf',
      },
      {
        labelKey: 'invites.materials.brochure',
        url: '/ByteCookie_Info_DE.pdf',
        filename: 'ByteCookie_Info_DE.pdf',
      },
    ];
    const invitesResponse = reactive<InviteCodesResponse>({ inviteCodes: [] });
    const affiliateCheckbox = ref(false);
    const affiliateCodeText = ref<HTMLElement>();
    const affiliateLinkInput = ref<HTMLInputElement>();
    const affiliateFootnotesTarget = ref<HTMLElement | null>(null);
    const copiedField = ref<'code' | 'link' | null>(null);
    const isCalcExamplePopupOpen = ref(false);
    const isInvitesLoading = ref(true);
    const invitesLoadFailed = ref(false);
    let copiedPopupTimeout: ReturnType<typeof setTimeout> | null = null;
    const isSigningAffiliateContract = ref(false);
    const { tUserFallback: t } = useUser('self');
    const { setPdf, showPopup, hidePopup } = usePopupState();
    const { loadSettings } = useSettings();
    const { globalErrorMessage } = usePageState();

    const loadInvites = async () => {
      isInvitesLoading.value = true;
      invitesLoadFailed.value = false;
      try {
        const inviteCodes = (await getInviteCodesOrThrow())?.inviteCodes ?? [];
        inviteCodes.sort((a: InviteCode, b: InviteCode) => sortComp(b.userMarkedAsUsed, a.userMarkedAsUsed) * 2 + sortComp(a.userMarkedAsUsedTs, b.userMarkedAsUsedTs));
        invitesResponse.inviteCodes = inviteCodes;
      } catch (error) {
        invitesLoadFailed.value = true;
        throw error;
      } finally {
        isInvitesLoading.value = false;
      }
    };

    const isAffiliateContractPossible = computed<boolean>(() => invitesResponse.inviteCodes.some((code) => code.affiliate));
    const affiliateCode = computed<string | null>(() => invitesResponse.inviteCodes.find((code) => code.affiliate && code.inviteCode !== '******')?.inviteCode ?? null);
    const affiliateCodeUrl = computed<string>(() => (affiliateCode.value ? getInviteCodeUrl(affiliateCode.value) : ''));
    const partnerCopyKey = computed<string>(() => {
      if (affiliateCode.value) return 'invites.partner.copy-partner';
      return 'invites.partner.copy';
    });
    const isAffiliateBeforeMode = computed<boolean>(() => !affiliateCode.value && isAffiliateContractPossible.value);

    const downloadAffiliateContract = async () => {
      try {
        showPopup();
        const contract = await getAffiliateContract();
        setPdf(contract.attachment, contract.filename);
      } catch (error) {
        hidePopup();
        console.error('View affiliate contract failed', error);
      }
    };

    const viewMaterial = async (material: MaterialItem) => {
      try {
        showPopup();
        const response = await fetch(material.url);
        if (!response.ok) {
          throw new Error(`Material request failed with status ${response.status}`);
        }
        const blob = await response.blob();
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          setPdf(base64, material.filename);
        };
      } catch (error) {
        hidePopup();
        console.error('View material failed', error);
      }
    };

    const downloadMaterial = (material: MaterialItem) => {
      const link = document.createElement('a');
      link.href = material.url;
      link.setAttribute('download', material.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    };

    const selectAffiliateCode = () => {
      if (!affiliateCodeText.value) return;
      const selection = window.getSelection();
      if (!selection) return;

      const range = document.createRange();
      range.selectNodeContents(affiliateCodeText.value);
      selection.removeAllRanges();
      selection.addRange(range);
    };

    const selectAffiliateLink = () => {
      if (!affiliateLinkInput.value) return;
      affiliateLinkInput.value.focus();
      affiliateLinkInput.value.select();
      affiliateLinkInput.value.setSelectionRange(0, affiliateLinkInput.value.value.length);
    };

    const showCopiedPopup = (field: 'code' | 'link') => {
      copiedField.value = field;
      if (copiedPopupTimeout) clearTimeout(copiedPopupTimeout);
      copiedPopupTimeout = setTimeout(() => {
        copiedField.value = null;
      }, 1400);
    };

    const openCalcExamplePopup = () => {
      isCalcExamplePopupOpen.value = true;
    };

    const closeCalcExamplePopup = () => {
      isCalcExamplePopupOpen.value = false;
    };

    const scrollToAffiliateFootnotes = () => {
      affiliateFootnotesTarget.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const copyToClipboard = async (value: string): Promise<boolean> => {
      if (!value) return false;

      try {
        if (window.isSecureContext && navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(value);
          return true;
        }
        return copy(value);
      } catch (error) {
        const copied = copy(value);
        console.error('Clipboard API copy failed, fallback copy was used', error);
        return copied;
      }
    };

    const copyAffiliateCode = async () => {
      const copied = await copyToClipboard(affiliateCode.value ?? '');
      if (copied) showCopiedPopup('code');
    };

    const copyAffiliateLink = async () => {
      const copied = await copyToClipboard(affiliateCodeUrl.value);
      if (copied) showCopiedPopup('link');
    };

    onBeforeUnmount(() => {
      if (copiedPopupTimeout) clearTimeout(copiedPopupTimeout);
    });

    const signAffiliateProgram = async () => {
      isSigningAffiliateContract.value = true;

      try {
        await signAffiliateContract();
        affiliateCheckbox.value = false;
        loadSettings();

        try {
          await loadInvites();
        } catch (error: any) {
          globalErrorMessage.value = {
            i18Key: 'invites.affiliate-invites.acceptReloadError',
            code: getAxiosResponseErrorCode(error) ?? 'UNKNOWN_ERROR',
            component: 'PartnerProgramCodes',
            withoutReload: true,
          };
          console.error('Reload invite codes after signing affiliate contract failed', error);
        }
      } catch (error: any) {
        globalErrorMessage.value = {
          i18Key: 'invites.affiliate-invites.acceptError',
          code: getAxiosResponseErrorCode(error) ?? 'UNKNOWN_ERROR',
          component: 'PartnerProgramCodes',
          withoutReload: true,
        };
        console.error('Sign affiliate contract failed', error);
      } finally {
        isSigningAffiliateContract.value = false;
      }
    };

    const loadInitialInvites = async () => {
      try {
        await loadInvites();
      } catch (error: any) {
        globalErrorMessage.value = {
          i18Key: 'invites.partner.loadError',
          code: getAxiosResponseErrorCode(error) ?? 'UNKNOWN_ERROR',
          component: 'PartnerProgramCodes',
          withoutReload: true,
        };
        console.error('Load Insider Program failed', error);
      }
    };

    onMounted(loadInitialInvites);

    return {
      affiliateCheckbox,
      affiliateCode,
      affiliateCodeText,
      affiliateFootnotesTarget,
      affiliateLinkInput,
      affiliateCodeUrl,
      closeCalcExamplePopup,
      copyAffiliateCode,
      copyAffiliateLink,
      copiedField,
      downloadMaterial,
      downloadAffiliateContract,
      isCalcExamplePopupOpen,
      isAffiliateContractPossible,
      isAffiliateBeforeMode,
      isInvitesLoading,
      invitesLoadFailed,
      isSigningAffiliateContract,
      materialItems,
      openCalcExamplePopup,
      partnerCopyKey,
      partnerBannerDesktop1x,
      partnerBannerDesktop2x,
      partnerBannerMobile1x,
      partnerBannerMobile2x,
      scrollToAffiliateFootnotes,
      selectAffiliateCode,
      selectAffiliateLink,
      signAffiliateProgram,
      viewMaterial,
      t
    };
  }
});
